import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PaDrawer } from "components/platform-admin";
import {
  createPlatformRole,
  fetchPlatformRoleCreateForm,
  fetchPlatformRoleDetail,
  updatePlatformRole,
} from "store/services/platformAdmin.services";
import PlatformPermissionGrid from "./PlatformPermissionGrid";
import {
  buildPermissionSections,
  canEditRoleName,
  getSelectedPermissionIds,
  OWNER_LOCKED_MESSAGE,
  togglePermissionSelection,
} from "./platformRoles.utils";

const PlatformRoleDrawer = ({ open, onClose, mode, role, onSaved }) => {
  const isEdit = mode === "edit";
  const [name, setName] = useState("");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) {
      setName("");
      setSections([]);
      setFormError("");
      return undefined;
    }

    let cancelled = false;

    const loadForm = async () => {
      setLoading(true);
      setFormError("");

      try {
        if (isEdit) {
          if (!role?.is_editable) {
            setFormError(OWNER_LOCKED_MESSAGE);
            setSections([]);
            return;
          }

          const data = await fetchPlatformRoleDetail(role.id);
          if (cancelled) {
            return;
          }

          setName(data?.role?.name || "");
          setSections(buildPermissionSections(data?.permission_groups));
          return;
        }

        const data = await fetchPlatformRoleCreateForm();
        if (cancelled) {
          return;
        }

        setName("");
        setSections(buildPermissionSections(data?.permission_groups));
      } catch (error) {
        if (!cancelled) {
          setFormError(error?.message || "Failed to load role form");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadForm();

    return () => {
      cancelled = true;
    };
  }, [open, isEdit, role]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError("Role name is required.");
      return;
    }

    const permissions = getSelectedPermissionIds(sections);
    if (permissions.length === 0) {
      setFormError("Select at least one permission.");
      return;
    }

    setSubmitting(true);

    try {
      if (isEdit) {
        const payload = { id: role.id, permissions };
        if (canEditRoleName(role)) {
          payload.name = trimmedName;
        }

        await updatePlatformRole(payload);
        toast.success("Platform role updated");
      } else {
        await createPlatformRole({ name: trimmedName, permissions });
        toast.success("Platform role created");
      }

      onSaved();
      onClose();
    } catch (error) {
      toast.error(error?.message || "Failed to save platform role");
    } finally {
      setSubmitting(false);
    }
  };

  const nameLocked = isEdit && role && !canEditRoleName(role);

  return (
    <PaDrawer
      open={open}
      onClose={onClose}
      widthClassName="max-w-2xl"
      title={isEdit ? "Edit platform role" : "Create platform role"}
      description={
        isEdit
          ? "Update permissions assigned to this role."
          : "Define a custom platform role and assign permissions."
      }
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="platform-role-form"
            disabled={submitting || loading || Boolean(isEdit && role && !role.is_editable)}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create role"}
          </button>
        </div>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-500">Loading permissions…</p>
      ) : (
        <form id="platform-role-form" className="space-y-5" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Role name</label>
            <input
              type="text"
              value={name}
              readOnly={nameLocked}
              onChange={(event) => setName(event.target.value)}
              className={`mt-1 block w-full rounded-lg border px-3 py-2 text-sm ${
                nameLocked ? "border-gray-200 bg-gray-50 text-gray-700" : "border-gray-300"
              }`}
            />
            {nameLocked ? (
              <p className="mt-1 text-xs text-gray-500">Built-in role names cannot be changed.</p>
            ) : null}
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700">Permissions</span>
            <p className="mt-1 text-xs text-gray-500">
              Permissions you do not hold are disabled and cannot be granted.
            </p>
            <div className="mt-3">
              <PlatformPermissionGrid
                sections={sections}
                onToggle={(permissionId) =>
                  setSections((prev) => togglePermissionSelection(prev, permissionId))
                }
              />
            </div>
          </div>

          {formError ? <p className="text-sm text-red-600">{formError}</p> : null}
        </form>
      )}
    </PaDrawer>
  );
};

export default PlatformRoleDrawer;
